import React, { useCallback } from 'react';
import { TLinkedModel, TUpsertISRProjectDataModelsInput } from "../../Pages/DefineModel/model";
import TextField from 'Components/DesignSystem/TextField/TextField';
import Dropdown from 'Components/DesignSystem/Dropdown/Dropdown';
import {Delete, CrossCircle, Cross, Unlink} from 'Components/DesignSystem/Iconography/Icons';
import { MenuDownIcon } from 'assests/GmsIcons';

type ModelOption = {
    value: string;
    label: string;
};

type LinkedModelsRowProps = {
    linkedModel: TLinkedModel;
    modelOptions: ModelOption[];
    modelsLoading: boolean;
    onDelete: (rowId: string) => void;
    onUpdate: (rowId: string, updates: Partial<TLinkedModel>) => void;
    onOpenFieldSelector: (rowId: string) => void;
    userProjectDataModels: TUpsertISRProjectDataModelsInput;
}

const LinkedModelsRow = ({
                              linkedModel,
                              modelOptions,
                              modelsLoading,
                              onDelete,
                              onUpdate,
                              onOpenFieldSelector,
                              userProjectDataModels
                          }: LinkedModelsRowProps) => {

    const handleModelChange = useCallback((value: string | number) => {
        const modelId = String(value);
        const name = modelOptions.find(m => m.value === modelId)?.label;
        onUpdate(linkedModel.rowId, { id: modelId, name });
    }, [linkedModel.rowId, modelOptions, onUpdate]);

    const handleDescriptionChange = useCallback((value: string) => {
        onUpdate(linkedModel.rowId, { description: value });
    }, [linkedModel.rowId, onUpdate]);

    const handleFieldClick = useCallback(() => {
        onOpenFieldSelector(linkedModel.rowId);
    }, [linkedModel.rowId, onOpenFieldSelector]);

    const handleDeleteClick = useCallback(() => {
        onDelete(linkedModel.rowId);
    }, [linkedModel.rowId, onDelete]);

    const handleClearCondition = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        onUpdate(linkedModel.rowId, { condition: undefined });
    }, [linkedModel.rowId, onUpdate]);

    function getFieldName(subModelId?: string, fieldId?: string): string | null {
        if (!subModelId || !fieldId) return null;
        const subModel = userProjectDataModels.project.dataModels.find(dm => dm.id === subModelId);
        const field = subModel?.data.fields.find(f => f.id === fieldId);
        return `${subModel?.data?.name ?? ''}.${field?.name ?? ''}`;
    }

    return (
        <tr id={`linked-model-row-${linkedModel.rowId}`}>
            {/* Model Name */}
            <td className="tw-px-5 tw-py-2 tw-align-top">
                <div id={`linked-model-name-${linkedModel.rowId}`}>
                    <Dropdown
                        label=""
                        options={modelOptions}
                        value={linkedModel.id}
                        onChange={handleModelChange}
                        placeholder={modelsLoading ? 'Loading models...' : 'Select a model…'}
                        disabled={modelsLoading}
                        usePortal
                    />
                </div>
            </td>

            {/* Description */}
            <td className="tw-px-4 tw-py-2 tw-align-top">
                <div id={`linked-model-description-${linkedModel.rowId}`}>
                    <TextField
                        label=""
                        value={linkedModel.description ?? ''}
                        onChange={handleDescriptionChange}
                        placeholder="Enter description (optional)"
                    />
                </div>
            </td>

            {/* Condition Field - Styled as Dropdown, matching InputParameterRow's Mapped-to field */}
            <td className="tw-px-4 tw-py-2 tw-align-top">
                <button
                    id={`linked-model-field-btn-${linkedModel.rowId}`}
                    onClick={handleFieldClick}
                    className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-edge tw-rounded-ctl tw-text-body-primary tw-text-left tw-flex tw-items-center tw-justify-between tw-transition-colors tw-bg-surface hover:tw-border-edge-strong focus:tw-outline-none focus:tw-border-accent-hover focus:tw-ring-1 focus:tw-ring-accent-hover"
                >
                    <span
                        title={linkedModel.condition?.fieldId ? getFieldName(linkedModel.condition?.subModelId, linkedModel.condition?.fieldId) ?? undefined : undefined}
                        className={`tw-flex-1 tw-min-w-0 tw-truncate ${linkedModel.condition?.fieldId ? 'tw-text-grey-900' : 'tw-text-grey-500'}`}
                    >
                        {linkedModel.condition?.fieldId ? getFieldName(linkedModel.condition?.subModelId, linkedModel.condition?.fieldId) : 'Add Condition Field'}
                    </span>
                    {linkedModel.condition?.fieldId ? (
                        <span
                            id={`linked-model-field-clear-${linkedModel.rowId}`}
                            role="button"
                            aria-label="Clear condition field"
                            title="Clear condition field"
                            onClick={handleClearCondition}
                            className="tw-flex tw-items-center tw-justify-center tw-text-grey-500 hover:tw-text-red-500 tw-transition-colors tw-ml-2 tw-shrink-0"
                        >
                            <CrossCircle />
                        </span>
                    ) : (
                        <MenuDownIcon />
                    )}
                </button>
            </td>

            {/* Actions */}
            <td className="tw-px-4 tw-py-2 tw-text-center tw-align-top">
                <div className="tw-pt-0.5">
                    <button
                        id={`linked-model-delete-btn-${linkedModel.rowId}`}
                        onClick={handleDeleteClick}
                        className="tw-inline-flex tw-items-center tw-justify-center tw-text-ink tw-opacity-60 hover:tw-text-red-500 hover:tw-opacity-100 tw-transition-all"
                        title="Unlink model"
                    >
                        <Unlink />
                    </button>
                </div>
            </td>
        </tr>
    );
};

export default LinkedModelsRow;
